import { useState } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Search from '@/icons/Search';

import QuestionIcon from '@/icons/Help/QuestionIcon';
import AccountIcon from '@/icons/Help/AccountIcon';
import TicketIcon from '@/icons/Help/TicketIcon';
import PaymentIcon from '@/icons/Help/PaymentIcon';
import TermsIcon from '@/icons/Help/TermsIcon';
import OrdersIcon from '@/icons/Help/OrdersIcon';
import MarketingIcon from '@/icons/Help/MarketingIcon';
import CalendarIcon from '@/icons/Help/CalendarIcon';
import { useAuth } from '@/contexts/auth-context';
import { IconChevronDown as ChevronDown } from '@tabler/icons-react';
import MobileBottomNav from '@/components/MobileBottomNav';
import { EmptyState, PageHero, bodyMuted, chip, chipActive, chipIdle, pageBg, wrap } from '@/components/ui/design';

const faqItem = 'overflow-hidden rounded-3xl bg-grey99 ring-1 ring-black/[0.04] transition-colors dark:bg-white/[0.04] dark:ring-white/[0.06]';
const faqChevronWrap = 'flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white ring-1 ring-black/[0.04] dark:bg-white/[0.06] dark:ring-white/10';

const HelpPage = () => {
    const [activeTab, setActiveTab] = useState<'attending' | 'organizing'>('attending');
    const [activeCategory, setActiveCategory] = useState('faq');
    const [searchQuery, setSearchQuery] = useState('');
    const [expandedQuestions, setExpandedQuestions] = useState<Set<number>>(new Set());
    const { user, isAuthenticated } = useAuth();

    const attendingCategories = [
        { id: 'faq', name: 'FAQ', icon: <QuestionIcon color={activeCategory === 'faq' ? '#007B8C' : '#465050'} />, active: activeCategory === 'faq' },
        { id: 'account', name: 'Your Account', icon: <AccountIcon color={activeCategory === 'account' ? '#007B8C' : '#465050'} />, active: activeCategory === 'account' },
        { id: 'tickets', name: 'Your Tickets', icon: <TicketIcon color={activeCategory === 'tickets' ? '#007B8C' : '#465050'} />, active: activeCategory === 'tickets' },
        { id: 'payment', name: 'Payment and Refund', icon: <PaymentIcon color={activeCategory === 'payment' ? '#007B8C' : '#465050'} />, active: activeCategory === 'payment' },
        { id: 'terms', name: 'Terms & Policies', icon: <TermsIcon color={activeCategory === 'terms' ? '#007B8C' : '#465050'} />, active: activeCategory === 'terms' },
    ];

    const organizingCategories = [
        { id: 'faq', name: 'FAQ', icon: <QuestionIcon color={activeCategory === 'faq' ? '#007B8C' : '#465050'} />, active: activeCategory === 'faq' },
        { id: 'account', name: 'Your Account', icon: <AccountIcon color={activeCategory === 'account' ? '#007B8C' : '#465050'} />, active: activeCategory === 'account' },
        { id: 'creating', name: 'Creating an Event', icon: <CalendarIcon color={activeCategory === 'creating' ? '#007B8C' : '#465050'} />, active: activeCategory === 'creating' },
        { id: 'marketing', name: 'Marketing an Event', icon: <MarketingIcon color={activeCategory === 'marketing' ? '#007B8C' : '#465050'} />, active: activeCategory === 'marketing' },
        { id: 'orders', name: 'Managing Orders', icon: <OrdersIcon color={activeCategory === 'orders' ? '#007B8C' : '#465050'} />, active: activeCategory === 'orders' },
        { id: 'payouts', name: 'Payouts and Taxes', icon: <PaymentIcon color={activeCategory === 'payouts' ? '#007B8C' : '#465050'} />, active: activeCategory === 'payouts' },
        { id: 'terms', name: 'Terms & Policies', icon: <TermsIcon color={activeCategory === 'terms' ? '#007B8C' : '#465050'} />, active: activeCategory === 'terms' },
    ];

    const attendingQuestions = {
        faq: [
            {
                question: 'How to update my email address',
                answer: 'To update your email address, go to your Profile page and click on the "Account" tab. In the Account section, you can update your profile information including your name and address. Note that email changes may require verification through a confirmation email.'
            },
            {
                question: 'How do I change my profile settings',
                answer: 'Navigate to your Profile page and click on the "Account" tab. Here you can update your name, location, address details, and profile picture. You can also use Google Maps integration to set your precise location. Changes are saved automatically when you click "Save Changes".'
            },
            {
                question: 'Where to find my tickets?',
                answer: 'Your tickets are available in the "Tickets" tab of your Profile page. You can also access them through the confirmation email sent after purchase. Each ticket includes a QR code for event entry and shows the event details, date, and venue information.'
            },
            {
                question: 'How to contact the event organizer?',
                answer: 'You can contact the event organizer through the event details page. Look for contact information in the event description or use the messaging system if you\'re an organizer. For general support, you can reach out through our contact page or help system.'
            },
            {
                question: 'How to buy tickets for others?',
                answer: 'When purchasing tickets, you can add custom attendee information during checkout. Enable "Use custom info" in the cart to enter different names and email addresses for each ticket. Each person will receive their own ticket via email with their name on it.'
            },
            {
                question: 'What\'s a guest user?',
                answer: 'A guest user can purchase tickets without creating an account. They receive tickets via email and can access them using the order confirmation. However, they won\'t have access to account features like order history, profile management, or saved payment methods.'
            },
            {
                question: 'Can I transfer my ticket to someone else?',
                answer: 'Currently, ticket transfers are handled through the event organizer. Contact the organizer directly if you need to transfer your ticket to someone else. They can assist with updating attendee information and ensuring the new attendee receives the ticket details.'
            },
        ],
        account: [
            {
                question: 'How to update my profile information',
                answer: 'Go to your Profile page and click on the "Account" tab. Here you can update your name, location, and address details. You can also upload a new profile picture and use Google Maps to set your precise location. Click "Save Changes" to update your information.'
            },
            {
                question: 'How to change my password',
                answer: 'Password changes are handled through the login system. If you need to reset your password, use the "Forgot Password" option on the login page. You\'ll receive an email with instructions to create a new password.'
            },
            {
                question: 'How to manage my account settings',
                answer: 'In your Profile page, you can access different tabs: Tickets (view your tickets), Events (if you\'re an organizer), Payments (view payment history), Account (profile settings), and Settings (notification preferences). Each section provides specific management options.'
            },
        ],
        tickets: [
            {
                question: 'Where are my tickets located?',
                answer: 'Your tickets are available in the "Tickets" tab of your Profile page. You can also access them through the confirmation email sent after purchase. Each ticket includes a QR code for event entry and shows event details, date, venue, and attendee information.'
            },
            {
                question: 'How to download my tickets',
                answer: 'Your tickets are automatically generated with QR codes and can be accessed through your Profile page under the "Tickets" tab. You can view ticket details, QR codes, and event information. Tickets are also sent via email for easy access.'
            },
            {
                question: 'Can I get a refund?',
                answer: 'Refund eligibility depends on the event organizer\'s refund policy and timing. Check the event\'s refund terms before purchasing. If refunds are allowed, contact the event organizer directly or use the refund request system if available for your order.'
            },
        ],
        payment: [
            {
                question: 'What payment methods are accepted?',
                answer: 'EventUp.ai accepts all major credit cards (Visa, MasterCard, American Express, Discover) and processes payments securely through Stripe. Payments are processed immediately upon checkout completion.'
            },
            {
                question: 'How to request a refund',
                answer: 'Refund requests are handled by the event organizer. Contact the organizer directly through the event details page or use the refund system if available. Refund eligibility depends on the event\'s refund policy and timing restrictions.'
            },
            {
                question: 'Payment processing time',
                answer: 'Payments are processed immediately through Stripe. You\'ll receive a confirmation email once your payment is successful, and your tickets will be available in your Profile page under the "Tickets" tab.'
            },
        ],
        terms: [
            {
                question: 'Terms of service',
                answer: 'Our terms of service outline the rules and guidelines for using our platform. They cover user responsibilities, prohibited activities, intellectual property rights, and dispute resolution procedures. You can find the full terms on our website.'
            },
            {
                question: 'Privacy policy',
                answer: 'We are committed to protecting your privacy. Our privacy policy explains how we collect, use, and protect your personal information. We never sell your data to third parties and use industry-standard security measures.'
            },
            {
                question: 'Refund policy',
                answer: 'Refund policies are set by individual event organizers. Some events offer full refunds up to a certain date, while others may have no refund policy. Always check the refund terms before purchasing tickets.'
            },
        ],
    } as const;

    const organizingQuestions = {
        faq: [
            {
                question: 'How to edit my organization information?',
                answer: 'Go to your Profile page and click on the "Organization" tab. Here you can update your organization details, contact information, and branding. Changes will be reflected on your event pages and in search results.'
            },
            {
                question: 'How to create different types of tickets?',
                answer: 'When creating an event, go to the "Tickets" section in the event editor. Click "Add Ticket" to create different ticket types like General Admission, VIP, Early Bird, or Group discounts. Set pricing, quantity, sale periods, and descriptions for each ticket type.'
            },
            {
                question: 'How to manage payouts?',
                answer: 'Payouts are handled through Stripe Connect. Set up your payout account in the "Payments" section of your Profile. Payouts are typically processed 2-5 business days after your event, depending on your Stripe settings.'
            },
            {
                question: 'How to email my registered attendees?',
                answer: 'Use the "Messaging" tab in your Profile to communicate with attendees. You can send event updates, reminders, or important announcements. Target specific ticket holders or all attendees through the messaging system.'
            },
            {
                question: 'How can I add images to my event?',
                answer: 'In your event editor, go to the "Media" section and upload images. You can add a main banner image, social media images, and additional gallery photos. Use high-quality images that represent your event well.'
            },
            {
                question: 'How to issue a refund?',
                answer: 'Go to your event\'s "Orders" section, find the specific order, and use the refund system. You can process full or partial refunds based on your event\'s refund policy. Refunds are processed back to the original payment method.'
            },
        ],
        account: [
            {
                question: 'How to update organization profile',
                answer: 'Go to your Profile page and click on the "Organization" tab. Here you can update your organization name, description, contact information, and branding. These details will appear on your event pages and in search results.'
            },
            {
                question: 'How to manage team members',
                answer: 'Team management features are available in the "Organization" tab of your Profile. You can invite team members, assign roles, and manage permissions for different aspects of event management.'
            },
            {
                question: 'How to set up notifications',
                answer: 'Go to the "Settings" tab in your Profile to customize notification preferences. You can set up email notifications for new orders, attendee questions, payment confirmations, and event reminders.'
            },
        ],
        creating: [
            {
                question: 'How to create a new event?',
                answer: 'Go to your Profile page and click on the "Events" tab, then click "Create Event". Fill out the event details including title, description, date/time, location, and category. Add tickets, media, and configure settings before publishing.'
            },
            {
                question: 'How to set up event details?',
                answer: 'In the event editor, provide comprehensive information including event name, description, multiple time slots, venue details, and category. Add detailed descriptions to help attendees understand what to expect from your event.'
            },
            {
                question: 'How to add event images?',
                answer: 'In the event editor, go to the "Media" section to upload images. Add a main banner image, social media images, and additional gallery photos. Use high-quality images that showcase your event and attract attendees.'
            },
            {
                question: 'How to configure ticket types?',
                answer: 'In the "Tickets" section of the event editor, create different ticket types with varying prices, quantities, and descriptions. Set sale periods, refund policies, and special requirements for each ticket type.'
            },
        ],
        marketing: [
            {
                question: 'How to promote my event?',
                answer: 'Use the "Messaging" tab in your Profile to send promotional emails to attendees. Share your event on social media platforms and use the built-in sharing features. Create engaging content and leverage analytics to track your marketing performance.'
            },
            {
                question: 'How to use social media marketing?',
                answer: 'Share your event on social platforms using the event\'s share buttons. Create engaging posts with event details, images, and hashtags. Use the messaging system to coordinate with attendees and build community around your event.'
            },
            {
                question: 'How to create promotional materials?',
                answer: 'Use high-quality images from your event\'s media gallery for promotional materials. Download event images and create your own flyers, social media graphics, and promotional content that matches your event branding.'
            },
            {
                question: 'How to track marketing performance?',
                answer: 'Monitor your event\'s performance through the analytics in your event dashboard. Track ticket sales, attendee engagement, and communication effectiveness. Use this data to optimize your marketing strategy and improve future events.'
            },
        ],
        orders: [
            {
                question: 'How to view and manage orders?',
                answer: 'Go to your event\'s dashboard and click on the "Orders" tab. Here you can view all orders, attendee information, payment status, and manage individual orders. Use the check-in system for event day management.'
            },
            {
                question: 'How to handle customer inquiries?',
                answer: 'Use the "Messaging" tab in your Profile to communicate with attendees. Respond to questions, provide support, and manage customer service efficiently. You can send bulk messages or individual responses.'
            },
            {
                question: 'How to process refunds?',
                answer: 'In your event\'s "Orders" section, find the specific order and use the refund system. You can process full or partial refunds based on your event\'s refund policy. Refunds are processed back to the original payment method.'
            },
            {
                question: 'How to export order data?',
                answer: 'Use the export features in your event\'s orders section to download attendee lists, sales reports, and financial data. Export data in various formats for analysis and record-keeping.'
            },
        ],
        payouts: [
            {
                question: 'How to set up payment methods?',
                answer: 'Set up your payout account through Stripe Connect in the "Payments" section of your Profile. Connect your bank account and configure payout preferences for secure transactions and automatic payouts.'
            },
            {
                question: 'How to track payouts?',
                answer: 'Monitor your payouts through Stripe Connect in the "Payments" section. View payout history, pending amounts, and transaction details. Set up notifications for payout confirmations and track your earnings.'
            },
            {
                question: 'How to handle tax reporting?',
                answer: 'Access financial reports in your event dashboard and Stripe Connect. Export sales summaries, attendee information, and revenue breakdowns for tax purposes. Use these reports for your accountant or tax software.'
            },
            {
                question: 'How to manage financial reports?',
                answer: 'Use the reporting features in your event dashboard to track sales, revenue, and expenses. Generate reports for specific time periods, analyze ticket sales trends, and monitor your event\'s financial performance.'
            },
        ],
        terms: [
            {
                question: 'Terms of service for organizers',
                answer: 'Our organizer terms outline your responsibilities, payment processing, refund policies, and platform usage guidelines. They cover event creation, attendee management, and compliance requirements for using our platform.'
            },
            {
                question: 'Privacy policy for organizers',
                answer: 'We protect both organizer and attendee data according to strict privacy standards. Our policy explains data collection, usage, and protection measures. We never share your data without consent and use encryption for all sensitive information.'
            },
            {
                question: 'Refund policy for organizers',
                answer: 'You can set your own refund policies for each event. Choose from no refunds, full refunds up to a certain date, or partial refunds. We provide tools to manage refund requests and process them efficiently.'
            },
            {
                question: 'Event cancellation policy',
                answer: 'If you need to cancel an event, use our cancellation tools to notify attendees and process refunds according to your policy. We provide templates for cancellation communications and help manage the process smoothly.'
            },
        ],
    } as const;

    const currentCategories = activeTab === 'attending' ? attendingCategories : organizingCategories;
    const currentQuestions = activeTab === 'attending' ? attendingQuestions : organizingQuestions;

    const headingText = isAuthenticated && user?.firstName
        ? `Hi ${user.firstName}, How can we help you?`
        : 'How can we help you?';

    // Derived, search-filtered questions for the active category
    const normalizedQuery = searchQuery.trim().toLowerCase();
    const filteredQuestions = (currentQuestions[activeCategory as keyof typeof currentQuestions] || []).filter((q: { question: string, answer: string }) =>
        normalizedQuery === '' ? true : q.question.toLowerCase().includes(normalizedQuery)
    );

    const handleQuestionClick = (index: number) => {
        setExpandedQuestions(prev => {
            // If the clicked question is already expanded, close it
            if (prev.has(index)) {
                return new Set();
            } else {
                // Otherwise, close all others and open this one
                return new Set([index]);
            }
        });
    };

    return (
        <>
            <Header />

            <main className={`min-h-screen ${pageBg}`}>
                <div className="relative z-10">
                    <PageHero eyebrow="Help Center" title={headingText}>
                        <div className="mt-8 max-w-[560px]">
                            <div className="relative">
                                <input
                                    type="text"
                                    placeholder="Search"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="h-14 w-full rounded-full bg-grey99 pl-14 pr-6 font-inter text-base text-grey10 ring-1 ring-black/[0.04] transition-shadow placeholder:text-grey60 focus:outline-none focus:ring-2 focus:ring-primary/50 dark:bg-white/[0.06] dark:text-white dark:ring-white/10 dark:placeholder:text-nightMuted dark:focus:ring-nightAccent/60"
                                />
                                <Search className="absolute left-5 top-1/2 h-5 w-5 -translate-y-1/2 transform text-grey50 dark:text-nightMuted" />
                            </div>
                        </div>

                        <div className="mt-8 mb-8 sm:mb-12">
                            <div className="inline-flex max-w-full overflow-x-auto scrollbar-hide rounded-full bg-grey99 p-1 ring-1 ring-black/[0.04] dark:bg-white/[0.06] dark:ring-white/[0.06]">
                                <button
                                    onClick={() => setActiveTab('attending')}
                                    className={`relative isolate h-10 whitespace-nowrap rounded-full px-4 font-inter text-sm font-semibold transition-all sm:px-5 ${activeTab === 'attending'
                                        ? 'text-white dark:text-nightBg'
                                        : 'text-grey40 hover:text-grey10 dark:text-nightMuted dark:hover:text-white'
                                        }`}
                                >
                                    Attending an Event
                                    {activeTab === 'attending' && (
                                        <div className="absolute inset-0 -z-10 rounded-full bg-grey10 shadow-sm dark:bg-white"></div>
                                    )}
                                </button>
                                <button
                                    onClick={() => setActiveTab('organizing')}
                                    className={`relative isolate h-10 whitespace-nowrap rounded-full px-4 font-inter text-sm font-semibold transition-all sm:px-5 ${activeTab === 'organizing'
                                        ? 'text-white dark:text-nightBg'
                                        : 'text-grey40 hover:text-grey10 dark:text-nightMuted dark:hover:text-white'
                                        }`}
                                >
                                    Organizing an Event
                                    {activeTab === 'organizing' && (
                                        <div className="absolute inset-0 -z-10 rounded-full bg-grey10 shadow-sm dark:bg-white"></div>
                                    )}
                                </button>
                            </div>
                        </div>
                    </PageHero>

                    <div className={`${wrap} pb-20`}>
                        <div className="">
                            <div className="md:hidden">
                                <div className="-mx-4 mb-6 flex items-center gap-2 overflow-x-auto px-4 scrollbar-hide">
                                    {currentCategories.map((category) => (
                                        <button
                                            key={category.id}
                                            onClick={() => setActiveCategory(category.id)}
                                            className={`${chip} flex items-center gap-2 pl-1 ${category.active ? chipActive : chipIdle}`}
                                        >
                                            <div className={`flex h-8 w-8 items-center justify-center rounded-full [&>svg]:h-[18px] [&>svg]:w-[18px] ${category.active
                                                ? 'bg-white'
                                                : 'bg-white dark:bg-white/90'
                                                }`}>
                                                {category.icon}
                                            </div>
                                            <div className="pr-1">
                                                <span className="whitespace-nowrap font-inter text-sm font-semibold">
                                                    {category.name}
                                                </span>
                                            </div>
                                        </button>
                                    ))}
                                </div>

                                <div className="space-y-3">
                                    {filteredQuestions.length === 0 ? (
                                        <EmptyState title="No results found." />
                                    ) : filteredQuestions.map((question, index) => (
                                        <div key={index} className={faqItem}>
                                            <button
                                                onClick={() => handleQuestionClick(index)}
                                                className="group flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
                                            >
                                                <span className="text-left font-inter text-base font-semibold text-grey10 transition-colors group-hover:text-primary dark:text-white dark:group-hover:text-nightAccent">
                                                    {question.question}
                                                </span>
                                                <span className={faqChevronWrap}>
                                                    <ChevronDown
                                                        className={`h-4 w-4 text-grey40 transition-transform dark:text-nightMuted ${expandedQuestions.has(index) ? 'rotate-180' : ''
                                                            }`}
                                                    />
                                                </span>
                                            </button>
                                            {expandedQuestions.has(index) && (
                                                <div className="px-5 pb-5">
                                                    <p className={bodyMuted}>
                                                        {question.answer}
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="hidden md:flex md:gap-12">
                                <div className="sticky top-24 w-[260px] flex-shrink-0 self-start space-y-2">
                                    {currentCategories.map((category) => (
                                        <button
                                            key={category.id}
                                            onClick={() => setActiveCategory(category.id)}
                                            className={`flex h-12 w-full items-center gap-3 rounded-full pl-2 pr-5 text-left transition-colors ${category.active ? chipActive : 'text-grey20 hover:bg-grey99 dark:text-nightTextSecondary dark:hover:bg-white/[0.06]'}`}
                                        >
                                            <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full [&>svg]:h-[18px] [&>svg]:w-[18px] ${category.active
                                                ? 'bg-white'
                                                : 'bg-grey99 dark:bg-white/90'
                                                }`}>
                                                {category.icon}
                                            </div>
                                            <div className="min-w-0">
                                                <span className="font-inter text-[15px] font-semibold">
                                                    {category.name}
                                                </span>
                                            </div>
                                        </button>
                                    ))}
                                </div>

                                <div className="flex-1 min-w-0">
                                    <div className="space-y-3">
                                        {filteredQuestions.length === 0 ? (
                                            <EmptyState title="No results found." />
                                        ) : filteredQuestions.map((question, index) => (
                                            <div key={index} className={faqItem}>
                                                <button
                                                    onClick={() => handleQuestionClick(index)}
                                                    className="group flex w-full items-center justify-between gap-4 px-6 py-5 text-left"
                                                >
                                                    <span className="text-left font-inter text-base font-semibold text-grey10 transition-colors group-hover:text-primary dark:text-white dark:group-hover:text-nightAccent sm:text-[17px]">
                                                        {question.question}
                                                    </span>
                                                    <span className={faqChevronWrap}>
                                                        <ChevronDown
                                                            className={`h-4 w-4 text-grey40 transition-transform dark:text-nightMuted ${expandedQuestions.has(index) ? 'rotate-180' : ''
                                                                }`}
                                                        />
                                                    </span>
                                                </button>
                                                {expandedQuestions.has(index) && (
                                                    <div className="px-6 pb-6">
                                                        <p className={bodyMuted}>
                                                            {question.answer}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            <Footer hasMobileNav />
            <MobileBottomNav />
        </>
    );
};

export default HelpPage;
